import { useLoggerStore } from '@/stores/LoggerStore';
import { useFt8Store } from '@/stores/Ft8Store';
import QsoService from '@/services/QsoService';
import type { QsoType, QsoLoggedMessage, AdifRecordType } from '@/types';
import { wsjtXLogger } from '@/utils/loggers';
import { findBandByFrequency } from '@/utils/helpers';
import { modesAreEquivalent } from '@/utils/mode';
import { convertAdifToJson } from '@/transformers/AdifToJsonTransformer';
import {
  extractRefsFromAdifRecord,
  resolveTheirRefs,
  type ExtractedRefs
} from '@/services/ft8RefExtraction';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

interface PendingAdifEntry {
  call: string;
  refs: ExtractedRefs;
  receivedAt: number;
}

// How long an unmatched LOGGED_ADIF stays available to enrich a QSO_LOGGED.
// POTACAT sends LOGGED_ADIF immediately before QSO_LOGGED, so this only needs
// to cover the brief gap between the two messages.
const ADIF_CORRELATION_WINDOW_MS = 10000;

export class Ft8Service {
  // LOGGED_ADIF references awaiting correlation with an upcoming QSO_LOGGED.
  private pendingAdif: PendingAdifEntry[] = [];

  private get loggerStore() {
    return useLoggerStore();
  }

  private get ft8Store() {
    return useFt8Store();
  }

  /**
   * Map WSJT-X QSO Logged message to HAMRS QSO format
   */
  mapQsoLoggedToHamrs(wsjtxQso: QsoLoggedMessage): Partial<QsoType> {
    // Convert frequency from Hz to MHz
    const frequency = Number(wsjtxQso.txFrequency) / 1000000;

    // Format date and time (ensure UTC)
    const date = dayjs.utc(wsjtxQso.dateTimeOff).format('YYYY-MM-DD');
    const time = dayjs.utc(wsjtxQso.dateTimeOff).format('HH:mm:ss');

    // Calculate band from frequency
    const freqMHz = Number(frequency.toFixed(6));
    const band = findBandByFrequency(freqMHz * 1000); // convert MHz to kHz

    // Build QSO object
    const qso: Partial<QsoType> = {
      // Worked station callsign from WSJT-X (DX Call)
      call: wsjtxQso.dxCall || '',
      qsoDate: date,
      timeOn: time,
      freq: freqMHz,
      band: band || '',
      mode: wsjtxQso.mode || '',
      rstSent: wsjtxQso.reportSent || '',
      rstRcvd: wsjtxQso.reportReceived || '',
      comment: wsjtxQso.comments || '',
      name: wsjtxQso.name || '',
      gridsquare: wsjtxQso.dxGrid || '',
      ft8: true,
    };

    // Add power if present and valid
    if (wsjtxQso.txPower) {
      const power = parseInt(wsjtxQso.txPower);
      if (!isNaN(power)) {
        qso.txPwr = power;
      }
    }

    // Map propagation mode if specified
    if (wsjtxQso.propagationMode) {
      qso.propMode = this.mapPropagationMode(wsjtxQso.propagationMode);
    }

    // Map my station and operator
    if (wsjtxQso.myCall) {
      qso.stationCallsign = wsjtxQso.myCall;
    }
    if (wsjtxQso.operatorCall) {
      qso.operator = wsjtxQso.operatorCall;
    } else if (wsjtxQso.myCall) {
      qso.operator = wsjtxQso.myCall;
    }
    if (wsjtxQso.myGrid) {
      qso.myGridsquare = wsjtxQso.myGrid;
    }

    return qso;
  }


  /**
   * Map WSJT-X propagation modes to HAMRS propagation modes
   */
  private mapPropagationMode(wsjtxMode: string): string {
    // WSJT-X uses standard ADIF propagation modes
    // Map to HAMRS propagation modes if different
    const modeMap: Record<string, string> = {
      'AS': 'AIRCRAFT_SCATTER',
      'AUR': 'AURORA',
      'BS': 'BACKSCATTER',
      'ECH': 'ECHOLINK',
      'ES': 'SPORADIC_E',
      'F2': 'F2_REFLECTION',
      'FAI': 'FIELD_ALIGNED_IRREGULARITIES',
      'INTERNET': 'INTERNET',
      'ION': 'IONOSCATTER',
      'IRL': 'IRLP',
      'MS': 'METEOR_SCATTER',
      'RPT': 'REPEATER',
      'RS': 'RAIN_SCATTER',
      'SAT': 'SATELLITE',
      'TEP': 'TRANS_EQUATORIAL',
      'TR': 'TROPOSPHERIC_DUCTING'
    };

    return modeMap[wsjtxMode] || wsjtxMode;
  }


  /**
   * Process a QSO received from WSJT-X
   */
  async processReceivedQso(data: any): Promise<boolean> {
    try {
      // Handle case where data might not have the expected structure
      if (!data || typeof data !== 'object') {
        console.error('Invalid data structure received:', data);
        return false;
      }

      // Log which message type we received for debugging
      wsjtXLogger('Received message type:', data.type);

      let qsoData: Partial<QsoType> | null = null;

      if (data.type === 'qsoLogged') {
        qsoData = this.mapQsoLoggedToHamrs(data.data as QsoLoggedMessage);
      } else {
        console.error('Unknown QSO data type:', data.type);
        return false;
      }

      if (!qsoData) {
        console.error('Failed to map QSO data');
        return false;
      }

      // Populate "Their Park" / "Their Summit" from the QSO_LOGGED exchange or a
      // correlated LOGGED_ADIF message, when the active logbook has those fields.
      this.applyTheirParkSummit(qsoData, data.data as QsoLoggedMessage);

      // Get current active logbook
      const currentLogbook = this.loggerStore.currentLogbook;
      if (!currentLogbook) {
        console.error('No active logbook to add QSO');
        this.ft8Store.connectionError = 'No active logbook selected';
        return false;
      }

      // Check for duplicates in existing QSOs
      const existingQsos = this.loggerStore.qsoList;
      // WSJT-X sends bare modes (e.g. "FT4"), but an existing QSO imported from
      // ADIF may carry the parent/submode composite ("MFSK---FT4"). Compare modes
      // by equivalence so the same QSO isn't logged twice across the two sources.
      const isDuplicate = existingQsos.some(existingQso =>
        existingQso.call === qsoData.call &&
        existingQso.qsoDate === qsoData.qsoDate &&
        existingQso.timeOn === qsoData.timeOn &&
        modesAreEquivalent(existingQso.mode, qsoData.mode)
      );

      if (isDuplicate) {
        wsjtXLogger('Skipping duplicate QSO:', qsoData.call, qsoData.qsoDate, qsoData.timeOn);
        return true; // Return success but don't log the duplicate
      }

      // If auto-accept is enabled, add the QSO directly
      if (this.ft8Store.autoAccept) {
        const savedQso = await QsoService.create({
          attrs: qsoData as QsoType,
          logbook: currentLogbook
        });

        // Update the store's QSO list to include the new QSO
        this.loggerStore.qsoList = [...this.loggerStore.qsoList, savedQso];
        wsjtXLogger('QSO successfully logged to HAMRS:', qsoData.call);
        return true;
      } else {
        // If auto-accept is disabled, we could implement a confirmation dialog
        // For now, we'll just add it since the UI component isn't ready yet
        const savedQso = await QsoService.create({
          attrs: qsoData as QsoType,
          logbook: currentLogbook
        });

        // Update the store's QSO list to include the new QSO
        this.loggerStore.qsoList = [...this.loggerStore.qsoList, savedQso];
        wsjtXLogger('QSO successfully logged to HAMRS:', qsoData.call);
        return true;
      }
    } catch (error) {
      console.error('Error processing WSJT-X QSO:', error);
      this.ft8Store.connectionError = error instanceof Error ? error.message : 'Failed to process QSO';
      return false;
    }
  }

  /**
   * Process a LOGGED_ADIF message received from WSJT-X. The park/summit
   * references are extracted and stashed so the matching QSO_LOGGED message can
   * use them as a fallback source. This never creates a QSO on its own.
   */
  async processReceivedAdif(data: any): Promise<void> {
    try {
      const adifText: unknown = data?.data?.adifText;
      if (!adifText || typeof adifText !== 'string') {
        return;
      }

      let records: AdifRecordType[] = [];
      try {
        records = convertAdifToJson(adifText);
      } catch (error) {
        console.error('Failed to parse WSJT-X LOGGED_ADIF payload:', error);
        return;
      }

      this.prunePendingAdif();

      for (const record of records) {
        const refs = extractRefsFromAdifRecord(record);
        if (!refs.potaRef && !refs.sotaRef) {
          continue;
        }
        const call = (record.call || '').toUpperCase();
        if (!call) {
          // An entry with no callsign can never be matched by a QSO_LOGGED, so
          // skip it rather than letting it linger until the window expires.
          continue;
        }
        this.pendingAdif.push({
          call,
          refs,
          receivedAt: Date.now()
        });
      }
    } catch (error) {
      console.error('Error processing WSJT-X LOGGED_ADIF:', error);
    }
  }

  /**
   * Populate "Their Park" / "Their Summit" on a mapped QSO using the documented
   * source precedence, but only for fields the active logbook actually defines.
   */
  private applyTheirParkSummit(qso: Partial<QsoType>, message: QsoLoggedMessage): void {
    const logger = this.loggerStore.logger;
    const hasPota = !!logger?.containsPotaFields();
    const hasSota = !!logger?.containsSotaFields();

    // Consume any pending ADIF that matches this QSO's callsign here -- before
    // the no-fields early return -- so it cannot bleed into a later QSO even
    // when this logbook defines neither field. A QSO with an empty callsign has
    // nothing to match (pending entries are always keyed by a non-empty call),
    // so consumePendingAdif correctly returns null in that case. In practice an
    // empty callsign never reaches here: handleQsoLogged filters those upstream.
    const adifRefs = this.consumePendingAdif(qso.call || '');

    if (!hasPota && !hasSota) {
      return;
    }

    const refs = resolveTheirRefs({
      exchangeReceived: message.exchangeReceived,
      adifRefs,
      hasPota,
      hasSota
    });

    if (refs.potaRef && !qso.potaRef) {
      qso.potaRef = refs.potaRef;
    }
    if (refs.sotaRef && !qso.sotaRef) {
      qso.sotaRef = refs.sotaRef;
    }
  }

  /**
   * Remove and return the references from any pending LOGGED_ADIF entries that
   * match the given callsign.
   */
  private consumePendingAdif(call: string): ExtractedRefs | null {
    this.prunePendingAdif();

    const target = call.toUpperCase();
    if (!target) {
      return null;
    }

    const merged: ExtractedRefs = {};
    let found = false;

    this.pendingAdif = this.pendingAdif.filter(entry => {
      if (entry.call !== target) {
        return true;
      }
      if (entry.refs.potaRef && !merged.potaRef) {
        merged.potaRef = entry.refs.potaRef;
      }
      if (entry.refs.sotaRef && !merged.sotaRef) {
        merged.sotaRef = entry.refs.sotaRef;
      }
      found = true;
      return false; // drop consumed entry
    });

    return found ? merged : null;
  }

  /** Drop pending LOGGED_ADIF entries older than the correlation window. */
  private prunePendingAdif(): void {
    const cutoff = Date.now() - ADIF_CORRELATION_WINDOW_MS;
    this.pendingAdif = this.pendingAdif.filter(entry => entry.receivedAt >= cutoff);
  }

}

// Create singleton instance
export const ft8Service = new Ft8Service();